/**
 * Chat sidebar action hooks (delete/rename/share/favourite) + Chat's own
 * attach-file picker reads. Follows src/queries/webhooks.js's template.
 *
 * usersContext.js's fetchChats() hasn't been migrated to TanStack Query yet
 * in this session (still a plain axios.get + setConversations), so callers
 * of the mutations below keep invoking that imperative refetch after the
 * mutation settles instead of invalidating a query key here — same
 * precedent as tasks.js's fetchTasksData.
 *
 * /chat/delete (GET) and /chat/add|remove/favourite (GET) are pre-existing
 * backend verbs — intentionally not "fixed" to DELETE/POST here.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, PUT } = ReqMethods;

// GET /chat/delete?id= — yes, a GET. Ported as-is.
export function useDeleteChat() {
  return useMutation({
    mutationFn: (id) => request(GET, "/chat/delete", { query: `id=${id}` }),
  });
}

export function useRenameChat() {
  return useMutation({
    mutationFn: ({ conversation_id, name, userId, locationId }) =>
      request(POST, "/chat/updateChatname", {
        payload: { conversation_id, name, userId, locationId, type: "user" },
      }),
  });
}

// Shared by chat-share and task-share flows (ShareChatModal) — branches on
// shareType internally, mirroring the endpoint/payload branch that used to
// live in the component.
//
// Task shares hit a dedicated POST that duplicates the Task per recipient
// (see ShareScheduledPrompt on the backend) rather than PUT /update/scheduled,
// which used to just append the recipient to the original document's userId
// array — so "sharing" a Task meant they edited the owner's own schedule and
// webhook instead of getting their own copy (CU-123yxuagqmq).
export function useShareChat() {
  return useMutation({
    mutationFn: ({ shareType, payload }) => {
      if (shareType === "task") {
        return request(POST, "/prompts/share/scheduled", { payload });
      }
      return request(PUT, "/chat/sharechat", { payload });
    },
  });
}

// GET /chat/add/favourite (uses chat_id) and GET /chat/remove/favourite
// (uses chatId) — different param names on the two verbs, preserved as-is.
export function useToggleFavouriteChat() {
  return useMutation({
    mutationFn: ({ add, chatId, userId, locationId }) => {
      const query = add
        ? `chat_id=${chatId}&user_id=${userId}&location_id=${locationId}`
        : `chatId=${chatId}&user_id=${userId}&location_id=${locationId}`;
      return request(GET, add ? "/chat/add/favourite" : "/chat/remove/favourite", {
        query,
      });
    },
  });
}

// GET /assistant/files/media-library — list part of Chat's attach-file
// "Add from Knowledge" media library. The fetch()-based download/convert
// step that used to chain off this axios call stays in the component
// exactly as-is (including its known undefined-files crash) — this hook
// only replaces the axios list fetch.
export function useMediaLibraryFiles(locationId) {
  return useQuery({
    queryKey: ["chatMediaLibraryFiles", locationId],
    queryFn: async () => {
      const res = await request(GET, "/assistant/files/media-library", {
        query: `altId=${locationId}&altType=location&objectId=${locationId}&whichObject=account`,
      });
      return res?.data?.files ?? null;
    },
    enabled: !!locationId,
  });
}

// GET /chat/assistants/files — Chat's own uploaded-files list ("Select a
// file" modal). `trigger` is `selectFileModel === true` from the component,
// included in the key purely to force a refetch on every toggle, matching
// the original `useEffect(..., [selectFileModel === true])` re-run.
export function useChatUploadFiles(profileId, userId, trigger) {
  return useQuery({
    queryKey: ["chatUploadFiles", profileId, userId, trigger],
    queryFn: async () => {
      const res = await request(GET, "/chat/assistants/files", {
        query: `profile_id=${profileId}&user_id=${userId}`,
      });
      return res?.data?.data ?? null;
    },
    enabled: !!profileId && !!userId,
  });
}
